import { useEffect, useState, type ReactNode } from 'react';
import {
  ApiError,
  createWorkLocation,
  disableWorkLocation,
  fetchWorkLocations,
  searchPlaces,
} from '../api/client';
import { MapPicker } from '../components/MapPicker';
import { describeGeoStatus, useGeolocation } from '../geo/useGeolocation';
import { useSession } from '../app/WorkforceContext';
import { useAsync } from '../app/useAsync';
import { presentError } from '../lib/error-messages';
import { hasAnyPermission } from '../lib/navigation';
import {
  formatCoordinate,
  gpsAccuracyWarning,
  validateDraft,
  type PlaceSuggestion,
} from '../lib/work-location-form';
import { EmptyState, ErrorCard, LoadingState } from '../states';

/**
 * Lokasi Kerja — menetapkan titik geofence dengan menunjuk peta.
 *
 * ADA KARENA deploy pilot 16 Agustus 2026 terpaksa memakai `--allow-outside-geofence`: koordinat
 * gudang tidak diketahui saat itu, dan menebaknya lebih berbahaya daripada mematikan radius.
 * Akibatnya setiap absensi tercatat "di luar radius" dan `is_location_verified` selalu `false` —
 * verifikasi lokasi praktis mati sampai titiknya ada.
 *
 * Menunjuk peta menyelesaikannya dalam hitungan detik, dan tidak menuntut siapa pun membuka
 * Google Maps lalu menyalin dua angka ke terminal.
 */
export function WorkLocationPage(): ReactNode {
  const session = useSession();
  const bolehUbah = hasAnyPermission(session.me.permissions, ['workforce.location.create']);

  const lokasi = useAsync(() => fetchWorkLocations(session.auth), [session.auth]);

  const [nama, setNama] = useState('');
  const [titik, setTitik] = useState<{ lat: number; lng: number } | null>(null);
  const [radius, setRadius] = useState('150');
  const [cari, setCari] = useState('');
  const [saran, setSaran] = useState<readonly PlaceSuggestion[]>([]);
  const [mengirim, setMengirim] = useState(false);
  const [galat, setGalat] = useState<ApiError | null>(null);
  const [pesan, setPesan] = useState<string | null>(null);
  /*
   * Akurasi pembacaan GPS terakhir, disimpan bersama titiknya.
   *
   * Bukan hiasan: admin yang berdiri di gudang menekan satu tombol dan menganggap titiknya pasti.
   * Perangkat hanya berkata "saya di suatu tempat dalam lingkaran ini", dan bila lingkaran itu
   * sebanding dengan radius geofence, pusatnya bisa meleset sejauh radius itu sendiri.
   */
  const [akurasiTitik, setAkurasiTitik] = useState<number | null>(null);
  const geo = useGeolocation();

  /*
   * Pencarian ditunda 600 ms setelah ketikan berhenti.
   *
   * Angka ini diambil apa adanya dari aplikasi absensi-kurir Emguard, yang memakainya karena
   * Nominatim adalah layanan gratis dengan batas pemakaian — mengirim satu permintaan per huruf
   * akan membuat alamat IP kita diblokir, dan blokirnya menimpa seluruh admin sekaligus.
   */
  useEffect(() => {
    const kata = cari.trim();
    if (kata.length < 3) {
      setSaran([]);
      return;
    }
    let dibatalkan = false;
    const timer = setTimeout(() => {
      void searchPlaces(kata)
        .then((hasil) => {
          if (!dibatalkan) setSaran(hasil);
        })
        .catch(() => {
          // Diabaikan dengan sengaja: pencarian nama tempat adalah kenyamanan, bukan syarat.
          // Admin tetap dapat menunjuk titik langsung di peta, dan itu jalur utamanya.
          if (!dibatalkan) setSaran([]);
        });
    }, 600);
    return () => {
      dibatalkan = true;
      clearTimeout(timer);
    };
  }, [cari]);

  const draf = {
    name: nama,
    latitude: titik?.lat ?? null,
    longitude: titik?.lng ?? null,
    radiusMeters: radius,
  };
  const masalah = validateDraft(draf);

  /*
   * Menyalin pembacaan GPS ke titik — hanya SEKALI per permintaan.
   *
   * `useGeolocation` memakai `watchPosition`, jadi pembacaannya terus diperbarui. Menyalinnya
   * setiap kali berubah akan memindahkan titik yang mungkin sudah digeser admin dengan tangan di
   * peta, tepat saat ia sedang memperbaikinya.
   */
  const [mintaGps, setMintaGps] = useState(false);
  useEffect(() => {
    if (!mintaGps) return;
    if (geo.status !== 'ready') return;
    const { latitude, longitude, accuracyMeters } = geo.location;
    if (latitude === null || longitude === null) return;
    setTitik({ lat: latitude, lng: longitude });
    setAkurasiTitik(accuracyMeters);
    setMintaGps(false);
  }, [mintaGps, geo.status, geo.location]);

  async function simpan(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (masalah !== null || titik === null) return;
    setMengirim(true);
    setGalat(null);
    setPesan(null);
    try {
      await createWorkLocation(session.auth, {
        name: nama.trim(),
        latitude: titik.lat,
        longitude: titik.lng,
        geofenceRadiusMeters: Number(radius),
      });
      setNama('');
      setTitik(null);
      setCari('');
      setSaran([]);
      setPesan('Lokasi kerja tersimpan. Absensi di dalam radius kini terverifikasi lokasinya.');
      lokasi.reload();
    } catch (sebab) {
      if (sebab instanceof ApiError) setGalat(sebab);
    } finally {
      setMengirim(false);
    }
  }

  async function nonaktifkan(id: string): Promise<void> {
    setGalat(null);
    try {
      await disableWorkLocation(session.auth, id);
      lokasi.reload();
    } catch (sebab) {
      if (sebab instanceof ApiError) setGalat(sebab);
    }
  }

  const peringatanAkurasi = titik ? gpsAccuracyWarning(akurasiTitik, Number(radius) || 150) : null;

  if (lokasi.state.status === 'loading') return <LoadingState label="Memuat lokasi kerja…" />;
  if (lokasi.state.status === 'error') {
    return <ErrorCard error={lokasi.state.error} onRetry={lokasi.reload} />;
  }

  const daftar = lokasi.state.data;

  return (
    <>
      {galat ? (
        <section className="card error">
          <h2>{presentError(galat.code, galat.status).title}</h2>
          <p className="muted small">{presentError(galat.code, galat.status).hint}</p>
        </section>
      ) : null}
      {pesan ? (
        <section className="card ok">
          <p>{pesan}</p>
        </section>
      ) : null}

      {bolehUbah ? (
        <section className="card">
          <h2>Tambah lokasi kerja</h2>

          <label htmlFor="nama">Nama lokasi</label>
          <input
            id="nama"
            value={nama}
            onChange={(e) => setNama(e.target.value)}
            placeholder="mis. Gudang Emguard"
          />

          <label htmlFor="cari">Cari nama tempat (opsional)</label>
          <input
            id="cari"
            value={cari}
            onChange={(e) => setCari(e.target.value)}
            placeholder="mis. Jalan Sudirman Jakarta"
          />
          {saran.length > 0 ? (
            <ul className="list">
              {saran.slice(0, 5).map((s) => (
                <li key={`${s.latitude},${s.longitude}`} className="list-item">
                  <button
                    type="button"
                    className="button-link"
                    onClick={() => {
                      setTitik({ lat: s.latitude, lng: s.longitude });
                      setSaran([]);
                    }}
                  >
                    {s.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          <MapPicker
            latitude={titik?.lat ?? null}
            longitude={titik?.lng ?? null}
            radiusMeters={Number(radius) || 150}
            onPick={(lat, lng) => setTitik({ lat, lng })}
          />
          <p className="muted small">
            {titik
              ? `Titik terpilih: ${formatCoordinate(titik.lat, titik.lng)}`
              : 'Ketuk peta untuk menandai titik.'}
          </p>

          {/*
            Tombol ini yang membuat menetapkan titik menjadi pekerjaan sepuluh detik: admin
            berdiri di gudang, menekan sekali, selesai. Mencari di peta menuntut ia mengenali
            atapnya dari udara — yang di kawasan pergudangan hampir mustahil.
          */}
          <button
            type="button"
            className="button-link"
            onClick={() => {
              setMintaGps(true);
              geo.retry();
            }}
            disabled={geo.status === 'locating'}
          >
            {geo.status === 'locating' ? 'Membaca lokasi…' : 'Pakai lokasi saya sekarang'}
          </button>
          {geo.status === 'denied' || geo.status === 'timeout' || geo.status === 'unavailable' ? (
            <p className="muted small">
              {describeGeoStatus(geo.status).title} — {describeGeoStatus(geo.status).hint} Anda
              tetap dapat menandai titiknya dengan mengetuk peta.
            </p>
          ) : null}
          {titik && akurasiTitik !== null ? (
            <p className="muted small">Pembacaan GPS akurat ±{Math.round(akurasiTitik)} m.</p>
          ) : null}
          {peringatanAkurasi ? <p className="warning">{peringatanAkurasi}</p> : null}

          <label htmlFor="radius">Radius (meter)</label>
          <input
            id="radius"
            inputMode="numeric"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
          />

          <form onSubmit={(e) => void simpan(e)}>
            {masalah ? <p className="warning">{masalah.message}</p> : null}
            <button
              type="submit"
              className="button-primary"
              disabled={masalah !== null || mengirim}
            >
              {mengirim ? 'Menyimpan…' : 'Simpan lokasi'}
            </button>
          </form>
        </section>
      ) : null}

      <section className="card">
        <div className="row space-between">
          <h2>Lokasi kerja</h2>
          <span className="badge">{daftar.length}</span>
        </div>
        {daftar.length === 0 ? (
          <EmptyState
            title="Belum ada lokasi kerja"
            hint="Tanpa lokasi, absensi tidak dapat diverifikasi radiusnya dan selalu tercatat di luar area."
          />
        ) : (
          <ul className="list">
            {daftar.map((l) => (
              <li key={l.id} className="list-item">
                <div className="row space-between">
                  <strong>{l.name}</strong>
                  <span className="badge">{l.isActive ? 'Aktif' : 'Nonaktif'}</span>
                </div>
                <p className="muted small">
                  Radius {l.geofenceRadiusMeters} m · {formatCoordinate(l.latitude, l.longitude)}
                </p>
                {bolehUbah && l.isActive ? (
                  <button
                    type="button"
                    className="button-link"
                    onClick={() => void nonaktifkan(l.id)}
                  >
                    Nonaktifkan
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
